import { useEffect, useRef, useState } from 'react';

export const useCarousel = (
  activeIndex: number,
  totalItems: number,
  setActiveIndex: (index: number) => void,
) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const touchStartRef = useRef({ x: 0, y: 0 });
  const swipeLockedRef = useRef(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect Breakpoints
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // Handle Centering Scroll
  useEffect(() => {
    const scroller = scrollerRef.current;
    const el = itemsRef.current[activeIndex];
    if (!scroller || !el) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const delta =
      elRect.left +
      elRect.width / 2 -
      (scrollerRect.left + scrollerRect.width / 2);

    requestAnimationFrame(() => {
      scroller.scrollBy({ left: delta, behavior: 'smooth' });
    });
  }, [activeIndex]);

  // Swipe Handlers
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
    swipeLockedRef.current = false;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (swipeLockedRef.current) return;
    const dx = e.touches[0].clientX - touchStartRef.current.x;
    const dy = e.touches[0].clientY - touchStartRef.current.y;

    if (Math.abs(dy) > Math.abs(dx) || Math.abs(dx) < 35) return;

    swipeLockedRef.current = true;
    if (dx < 0 && activeIndex < totalItems - 1) setActiveIndex(activeIndex + 1);
    else if (dx > 0 && activeIndex > 0) setActiveIndex(activeIndex - 1);
  };

  return {
    scrollerRef,
    itemsRef,
    isMobile,
    touchHandlers: {
      onTouchStart,
      onTouchMove,
      onTouchEnd: () => (swipeLockedRef.current = false),
    },
  };
};
